// src/components/admin/AdminLayout.js
import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import './AdminLayout.css';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../auth/AuthModal';
import BillingTabs from './BillingTabs';
import SiteTabs from './SiteTabs';

function AdminLayout({ title = 'Admin', children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch { }
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }); // 'dark' | 'light'
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('adminSidebarCollapsed') === 'true';
    } catch { }
    return false;
  }); // desktop: icons only
  const { user: currentUser, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' | 'reset'
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = React.useRef(null);
  const sidebarRef = React.useRef(null);

  const SIDEBAR_ACCORDION_KEY = 'adminSidebarAccordion';
  const [sidebarSectionsOpen, setSidebarSectionsOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_ACCORDION_KEY);
      if (!raw) return { general: true, sales: true, projects: true, site: true, billing: true };
      const parsed = JSON.parse(raw);
      return {
        general: parsed?.general !== false,
        sales: parsed?.sales !== false,
        projects: parsed?.projects !== false,
        site: parsed?.site !== false,
        billing: parsed?.billing !== false,
      };
    } catch {
      return { general: true, sales: true, projects: true, site: true, billing: true };
    }
  });

  // Track desktop vs mobile to control rendering/labels
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : true));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Hysteresis to avoid flipping near the breakpoint (scrollbar width changes)
    let current = window.innerWidth >= 1024;
    setIsDesktop(current);
    const onResize = () => {
      const w = window.innerWidth;
      if (current) {
        // Only switch to mobile if we go far below the breakpoint
        if (w < 1000) { current = false; setIsDesktop(false); }
      } else {
        // Only switch to desktop if we go far above the breakpoint
        if (w > 1040) { current = true; setIsDesktop(true); }
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // collapsed is now initialized from localStorage synchronously to prevent initial flash

  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const didMountRef = React.useRef(false);
  useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
    if (didMountRef.current) {
      try { localStorage.setItem('theme', theme); } catch { }
    } else {
      didMountRef.current = true;
    }
  }, [theme]);

  // Sync with other tabs/components via storage events
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'theme') {
        const val = e.newValue === 'dark' ? 'dark' : 'light';
        if (val !== theme) setTheme(val);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_ACCORDION_KEY, JSON.stringify(sidebarSectionsOpen));
    } catch { }
  }, [sidebarSectionsOpen]);

  const handleBurger = () => {
    const isDesktop = window.innerWidth >= 1024;
    if (isDesktop) {
      setCollapsed(v => !v);
    } else {
      setOpen(v => !v);
    }
  };

  const adminThemeClass = theme === 'dark' ? 'admin-dark' : 'admin-light';
  const isBilling = location.pathname.startsWith('/admin/facturation');
  const isSite = location.pathname.startsWith('/admin/site');

  useEffect(() => {
    if (collapsed) {
      setSidebarSectionsOpen({ general: true, sales: true, projects: true, site: true, billing: true });
      return;
    }
    setSidebarSectionsOpen((prev) => {
      const next = { ...prev };
      if (location.pathname.startsWith('/admin/site')) next.site = true;
      if (location.pathname.startsWith('/admin/facturation')) next.billing = true;
      if (location.pathname.startsWith('/admin/projects') || location.pathname.startsWith('/admin/tasks')) next.projects = true;
      if (location.pathname.startsWith('/admin/meetings')) next.sales = true;
      if (location.pathname === '/admin' || location.pathname.startsWith('/tableau-de-bord')) next.general = true;
      return next;
    });
  }, [location.pathname, collapsed]);

  const toggleSidebarSection = (key) => {
    if (collapsed) return;
    setSidebarSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sidebarClass = `admin-sidebar ${(!isDesktop && open) ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`;

  // Safety: never keep "open" on desktop to avoid any flicker
  useEffect(() => {
    if (isDesktop && open) setOpen(false);
  }, [isDesktop, open]);

  // Mobile-only: close the sidebar after route change
  useEffect(() => {
    if (!isDesktop) setOpen(false);
  }, [location.pathname, isDesktop]);

  // Persist sidebar scroll position across route changes
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    // Restore saved scroll position
    try {
      const raw = sessionStorage.getItem('adminSidebarScroll');
      const val = raw ? parseInt(raw, 10) : 0;
      if (!Number.isNaN(val)) el.scrollTop = val;
    } catch { }

    const onScroll = () => {
      try { sessionStorage.setItem('adminSidebarScroll', String(el.scrollTop)); } catch { }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); };
  }, []);

  // When route changes, re-apply last saved sidebar scroll
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const restore = () => {
      try {
        const raw = sessionStorage.getItem('adminSidebarScroll');
        const val = raw ? parseInt(raw, 10) : 0;
        if (!Number.isNaN(val)) el.scrollTop = val;
      } catch { }
    };
    // Restore immediately, then after layout to counter any auto-scroll from active NavLink
    restore();
    const raf1 = requestAnimationFrame(() => {
      restore();
      const raf2 = requestAnimationFrame(() => restore());
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [location.pathname]);

  return (
    <div className={`admin-shell ${adminThemeClass}`}>
      <aside ref={sidebarRef} className={sidebarClass}>
        <div className="brand">
          <button
            type="button"
            className="brand-toggle"
            onClick={() => {
              if (isDesktop) {
                setCollapsed(v => !v);
              } else {
                setOpen(v => !v);
              }
            }}
            aria-label={isDesktop ? (collapsed ? 'Ouvrir la sidebar' : 'Réduire la sidebar') : (open ? 'Fermer la sidebar' : 'Ouvrir la sidebar')}
            title={isDesktop ? (collapsed ? 'Ouvrir' : 'Réduire') : (open ? 'Fermer' : 'Ouvrir')}
          >
            {isDesktop ? (collapsed ? '▶' : '◀') : (open ? '◀' : '▶')}
          </button>
          {isDesktop ? (
            !collapsed && (<div className="title">Admin</div>)
          ) : (
            open && (<div className="title">Admin</div>)
          )}
        </div>

        <nav className="admin-nav">
          <div className="admin-section">
            <button type="button" className="label" onClick={() => toggleSidebarSection('general')} aria-expanded={!!sidebarSectionsOpen.general}>
              <span>Général</span>
              <span className="section-caret">▾</span>
            </button>
            <div className={"admin-section-body" + (sidebarSectionsOpen.general ? ' open' : '')}>
              <ul className="admin-menu">
                <li>
                  <NavLink to="/admin" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🏠</span>
                    <span className="item-label">Accueil</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/tableau-de-bord" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📊</span>
                    <span className="item-label">Mon tableau</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/messages" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">💬</span>
                    <span className="item-label">Messagerie</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/staff" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">👥</span>
                    <span className="item-label">Équipe (Staff)</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div className="admin-section">
            <button type="button" className="label" onClick={() => toggleSidebarSection('sales')} aria-expanded={!!sidebarSectionsOpen.sales}>
              <span>Ventes</span>
              <span className="section-caret">▾</span>
            </button>
            <div className={"admin-section-body" + (sidebarSectionsOpen.sales ? ' open' : '')}>
              <ul className="admin-menu">
                <li>
                  <NavLink to="/admin/sales/team" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🕵️‍♂️</span>
                    <span className="item-label">Équipe Commerciale</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/meetings" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📅</span>
                    <span className="item-label">Agenda Équipe</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div className="admin-section">
            <button type="button" className="label" onClick={() => toggleSidebarSection('projects')} aria-expanded={!!sidebarSectionsOpen.projects}>
              <span>Projets</span>
              <span className="section-caret">▾</span>
            </button>
            <div className={"admin-section-body" + (sidebarSectionsOpen.projects ? ' open' : '')}>
              <ul className="admin-menu">
                <li>
                  <NavLink to="/admin/projects" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📁</span>
                    <span className="item-label">Projets</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/tasks" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">✅</span>
                    <span className="item-label">Tâches</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div className="admin-section">
            <button type="button" className="label" onClick={() => toggleSidebarSection('site')} aria-expanded={!!sidebarSectionsOpen.site}>
              <span>Site</span>
              <span className="section-caret">▾</span>
            </button>
            <div className={"admin-section-body" + (sidebarSectionsOpen.site ? ' open' : '')}>
              <ul className="admin-menu">
                <li>
                  <NavLink to="/admin/site" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🧩</span>
                    <span className="item-label">Tableau de bord</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/accueil/slider" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🖼️</span>
                    <span className="item-label">Accueil · Slider</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/accueil/sections" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🧱</span>
                    <span className="item-label">Accueil · Sections</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/realisations" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📸</span>
                    <span className="item-label">Réalisations</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/realisations/categories" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🏷️</span>
                    <span className="item-label">Réalisations · Catégories</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/tarifs" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">💶</span>
                    <span className="item-label">Tarifs</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/tarifs/plans" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📋</span>
                    <span className="item-label">Tarifs · Plans</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/tarifs/options" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🧩</span>
                    <span className="item-label">Tarifs · Options</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/tarifs/comparatif" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📊</span>
                    <span className="item-label">Tarifs · Comparatif</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/tarifs/details" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🧾</span>
                    <span className="item-label">Tarifs · Détails</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/contact" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">✉️</span>
                    <span className="item-label">Contact</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/contact/reglages" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">⚙️</span>
                    <span className="item-label">Contact · Réglages</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/contact/messages" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📩</span>
                    <span className="item-label">Contact · Messages</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/annonces" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📣</span>
                    <span className="item-label">Promotions</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/taglines" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🏷️</span>
                    <span className="item-label">Annonces</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/effets" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">✨</span>
                    <span className="item-label">Effets Visuels</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/blog/articles" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📰</span>
                    <span className="item-label">Blog · Articles</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/blog/categories" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🗂️</span>
                    <span className="item-label">Blog · Catégories</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/site/equipe" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">👥</span>
                    <span className="item-label">Équipe</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div className="admin-section">
            <button type="button" className="label" onClick={() => toggleSidebarSection('billing')} aria-expanded={!!sidebarSectionsOpen.billing}>
              <span>Facturation</span>
              <span className="section-caret">▾</span>
            </button>
            <div className={"admin-section-body" + (sidebarSectionsOpen.billing ? ' open' : '')}>
              <ul className="admin-menu">
                <li>
                  <NavLink to="/admin/facturation" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">💼</span>
                    <span className="item-label">Tableau de bord</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/clients" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">👥</span>
                    <span className="item-label">Clients</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/devis" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🧾</span>
                    <span className="item-label">Devis</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/factures" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📄</span>
                    <span className="item-label">Factures</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/contrats" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📜</span>
                    <span className="item-label">Contrats</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/prestations" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🛠️</span>
                    <span className="item-label">Prestations</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/stock" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📦</span>
                    <span className="item-label">Stock</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/rendez-vous" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">📅</span>
                    <span className="item-label">Rendez-vous</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/historique" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">🕘</span>
                    <span className="item-label">Historique</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/facturation/parametres" className={({ isActive }) => isActive ? 'active' : ''}>
                    <span className="ico">⚙️</span>
                    <span className="item-label">Paramètres</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </aside>

      <main className={`admin-main ${collapsed ? 'collapsed' : ''}`}>
        <header className="admin-header">
          <div className="header-left">
            <button className="burger" onClick={handleBurger} aria-label="Menu">
              <img src="/images/logoo.png" alt="Logo Info Colab" />
            </button>
            <span className="burger-label">Menu</span>
            <div className="header-title">{title}</div>
          </div>
          <div className="search">
            <input placeholder="Recherche…" />
          </div>
          <div className="header-right">
            <Link to="/" className="header-pill header-home-link" aria-label="Retour au site" title="Retour au site">
              <span className="header-home-ico">🏠</span>
              <span className="header-home-label">Site</span>
            </Link>
            <button
              type="button"
              className="theme-toggle-btn"
              onClick={() => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'))}
              aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
              title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <div className="user-menu" ref={userMenuRef}>
              <button
                type="button"
                className="avatar-btn"
                onClick={() => {
                  if (!currentUser) {
                    setAuthMode('login');
                    setAuthOpen(true);
                    return;
                  }
                  setUserMenuOpen(v => !v);
                }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={currentUser ? 'Ouvrir le menu utilisateur' : 'Ouvrir le menu de connexion'}
              >
                {currentUser?.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="Avatar" className="avatar-img" />
                ) : (
                  <span className="avatar-fallback">👤</span>
                )}
              </button>
              {currentUser && userMenuOpen && (
                <div className="user-dropdown" role="menu">
                  <ul>
                    <li className="user-id-line">{currentUser.name || currentUser.email}</li>
                    <li><NavLink to="/mon-compte" onClick={() => setUserMenuOpen(false)}>Mon profil</NavLink></li>
                    <li><NavLink to="/tableau-de-bord" onClick={() => setUserMenuOpen(false)}>Tableau de bord</NavLink></li>
                    <li><NavLink to="/admin" onClick={() => setUserMenuOpen(false)}>Espace admin</NavLink></li>
                    <li><button type="button" className="logout-btn" onClick={() => { logout(); setUserMenuOpen(false); }}>Se déconnecter</button></li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className="admin-content">
          {isBilling && (
            <div className="billing-tabs-wrap">
              <BillingTabs />
            </div>
          )}
          {isSite && (
            <div className="site-tabs-wrap">
              <SiteTabs />
            </div>
          )}
          <div className="card">
            {children}
          </div>
        </div>
      </main>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} mode={authMode} onModeChange={setAuthMode} />
    </div>
  );
}

export default AdminLayout;
